import { ReactElement, useMemo, useState } from "react"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { SingleDatePicker } from "react-dates"
import moment from "moment/moment"

import { Button as WfpButton, Checkbox, Input, Loading, Modal, TextInput, Toggle } from "@wfp/ui"
import { iconAddOutline } from "@wfp/icons"

import { faCopy, faLock, faLockOpen, faPen, faTrash, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { DataBridgesApiKeyList } from "interfaces/dataInterfaces"
import {
  useCreateDataBridgesApiKeyMutation,
  useDeleteDataBridgesApiKeyMutation,
  useGetDataBridgesApiKeysQuery,
  useUpdateDataBridgesApiKeyMutation,
} from "services/core/apiKey"

import useCommon from "hooks/useCommon"
import { formatDate } from "utils/helpers"
import { DataBridgesApiKeyStatus } from "constants/constants"
import { isExpiredRecord } from "../../utils/dateHelper"

const StatusBadge = ({ statusId }: { statusId: number }): ReactElement => {
  const enabled = statusId === DataBridgesApiKeyStatus.ENABLED

  return (
    <span
      className={`rounded-md px-2 py-0.5 text-xs font-bold capitalize border border-solid min-w-[80px] text-center ${
        enabled ? "bg-green-100 text-green-700 border-green-500" : "bg-gray-200 text-gray-700 border-gray-500"
      }`}
    >
      {enabled ? "Enabled" : "Disabled"}
    </span>
  )
}

const DataBridgesApiKeysTab = (): ReactElement => {
  const { showSnackBar } = useCommon()
  const { data: rows = [], isLoading: isLoadingRows, isError: isRowsError } = useGetDataBridgesApiKeysQuery()
  const [createDataBridgesApiKey, { isLoading: isSavingDataBridgesApiKey }] = useCreateDataBridgesApiKeyMutation()
  const [updateDataBridgesApiKey, { isLoading: isUpdatingDataBridgesApiKey }] = useUpdateDataBridgesApiKeyMutation()
  const [deleteDataBridgesApiKey, { isLoading: isDeletingDataBridgesApiKey }] = useDeleteDataBridgesApiKeyMutation()

  const [deleteItem, setDeleteItem] = useState<DataBridgesApiKeyList | null>(null)

  const [actionItem, setActionItem] = useState<{ action: "add" | "edit"; item?: DataBridgesApiKeyList } | null>(null)
  const [form, setForm] = useState<{
    keyName: string
    expiryDate: string
    apiKeyStatusId: number
    piiaccess: boolean
  }>({
    keyName: "",
    expiryDate: "",
    apiKeyStatusId: DataBridgesApiKeyStatus.ENABLED,
    piiaccess: false,
  })
  const [errors, setErrors] = useState<{ keyName?: string; expiryDate?: string }>({})
  const [focused, setFocused] = useState<boolean>(false)

  const resetForm = (): void => {
    setForm({
      keyName: "",
      expiryDate: "",
      apiKeyStatusId: DataBridgesApiKeyStatus.ENABLED,
      piiaccess: false,
    })
    setErrors({})
  }

  const handleAdd = (): void => {
    resetForm()
    setActionItem({ action: "add" })
  }

  const handleEdit = (row: DataBridgesApiKeyList): void => {
    const normalizedDate = row.expiryDate || ""
    setForm({
      keyName: row.keyName || "",
      expiryDate: normalizedDate,
      apiKeyStatusId: row.apiKeyStatusId || DataBridgesApiKeyStatus.ENABLED,
      piiaccess: row.piiaccess || false,
    })
    setErrors({})
    setActionItem({ action: "edit", item: row })
  }

  const handleSave = async (): Promise<void> => {
    const currentErrors: { keyName?: string; expiryDate?: string; apiKeyStatusId?: string } = {}
    if (!form.keyName?.trim()) currentErrors.keyName = "Name is required"
    if (!form.expiryDate) currentErrors.expiryDate = "Expiration date is required"

    setErrors(currentErrors)
    if (Object.keys(currentErrors).length) return

    if (actionItem?.action === "edit" && actionItem.item) {
      try {
        await updateDataBridgesApiKey({
          apiKeyId: actionItem.item.apiKeyId,
          keyName: form.keyName.trim(),
          expiryDate: form.expiryDate,
          apiKeyStatusId: form.apiKeyStatusId,
          piiaccess: form.piiaccess,
        }).unwrap()

        showSnackBar("API key updated successfully")
      } catch (err) {
        showSnackBar("Unable to update API key", "error")
        return
      }
    } else {
      try {
        await createDataBridgesApiKey({
          keyName: form.keyName.trim(),
          expiryDate: form.expiryDate,
          apiKeyStatusId: form.apiKeyStatusId,
          piiaccess: form.piiaccess,
        }).unwrap()

        showSnackBar("API key added successfully")
      } catch (err) {
        showSnackBar("Unable to add API key", "error")
        return
      }
    }

    setActionItem(null)
    resetForm()
  }

  const handleDelete = (row: DataBridgesApiKeyList): void => {
    setDeleteItem(row)
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteItem?.apiKeyId) return

    try {
      await deleteDataBridgesApiKey(deleteItem.apiKeyId).unwrap()
      showSnackBar("API key deleted successfully")
      setDeleteItem(null)
    } catch (error) {
      showSnackBar("Unable to delete API key", "error")
    }
  }

  const handleCopy = async (value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value)
      showSnackBar("API key copied")
    } catch (error) {
      showSnackBar("Unable to copy API key", "error")
    }
  }

  const columns: ColumnShape<DataBridgesApiKeyList>[] = useMemo(
    () => [
      {
        key: "name",
        dataKey: "keyName",
        title: "Name",
        width: 250,
        flexGrow: 2,
      },
      {
        key: "key",
        dataKey: "apiKey",
        title: "Key",
        width: 180,
        flexGrow: 1,
        cellRenderer: ({ rowData }) => (
          <div className="d-flex align-center">
            <span className="mr-1 rounded-md border border-solid border-gray-300 bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
              {`${rowData.apiKey?.slice(0, 4)}••••`}
            </span>

            <FontAwesomeIcon
              icon={faCopy}
              className="text-[var(--interactive-01)] hover:text-[var(--interactive-02)] transition-colors cursor-pointer"
              onClick={() => handleCopy(rowData.apiKey)}
              title="Copy key"
            />
          </div>
        ),
      },
      {
        key: "createdDate",
        dataKey: "createDate",
        title: "Created Date",
        width: 180,
        flexGrow: 1,
        cellRenderer: ({ rowData }) => formatDate(rowData.createDate, "DD/MM/YYYY"),
      },
      {
        key: "expiredDate",
        dataKey: "expiryDate",
        title: "Expiration Date",
        width: 180,
        flexGrow: 1,
        cellRenderer: ({ rowData }) => {
          return (
            <span
              className={`min-w-[80px] text-center ${
                isExpiredRecord(rowData.expiryDate)
                  ? "bg-red-100 text-[var(--support-01)] border-red-500 border border-solid rounded-md px-2 py-0.5 text-xs font-bold"
                  : ""
              }`}
            >
              {formatDate(rowData.expiryDate, "DD/MM/YYYY")}
            </span>
          )
        },
      },
      {
        key: "status",
        dataKey: "apiKeyStatusId",
        title: "Status",
        width: 130,
        minWidth: 110,
        cellRenderer: ({ rowData }) => <StatusBadge statusId={rowData.apiKeyStatusId} />,
      },
      {
        key: "piiaccess",
        dataKey: "piiaccess",
        title: "PII",
        width: 40,
        minWidth: 40,
        cellRenderer: ({ rowData }) => (
          <FontAwesomeIcon
            icon={rowData.piiaccess ? faLockOpen : faLock}
            className={rowData.piiaccess ? "text-[var(--interactive-01)]" : "#333"}
            title={rowData.piiaccess ? "PII access enabled" : "PII access disabled"}
          />
        ),
      },
      {
        key: "actions",
        title: "",
        width: 120,
        align: Column.Alignment.RIGHT,
        cellRenderer: ({ rowData }) => (
          <div className="d-flex justify-end gap-x-1">
            <FontAwesomeIcon
              icon={faPen}
              className="p-1 rounded text-[var(--interactive-01)] hover:text-[var(--interactive-02)] transition-colors cursor-pointer"
              onClick={() => handleEdit(rowData)}
              title="Edit"
            />

            <FontAwesomeIcon
              icon={faTrash}
              title="Delete"
              className="p-1 rounded text-[var(--support-01)] hover:text-red-800 transition-colors cursor-pointer"
              onClick={() => handleDelete(rowData)}
            />
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <div>
      <div className="d-flex justify-end w-full">
        <WfpButton kind="primary" onClick={handleAdd} iconDescription="Add API key" icon={iconAddOutline} iconReverse>
          Add New API Key
        </WfpButton>
      </div>

      <div style={{ height: "calc(100vh - 240px)", minHeight: "60vh", marginTop: 12 }}>
        {isLoadingRows ? (
          <div className="w-full h-full d-flex align-center justify-center">
            <Loading withOverlay={false} />
          </div>
        ) : isRowsError ? (
          <div className="w-full h-full d-flex align-center justify-center flex-col">
            <div className="max-w-[420px] w-full rounded-md border border-solid border-red-200 bg-red-50 p-4 text-center">
              <div className="mb-2">
                <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-600 text-2xl" />
              </div>
              <p className="mb-1 text-red-700 font-semibold">Unable to load API keys</p>
              <p className="mb-3 text-sm text-red-700">Something went wrong while fetching your keys.</p>
            </div>
          </div>
        ) : (
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={rows}
                width={width}
                height={height}
                rowKey="apiKeyId"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        )}
      </div>

      <Modal
        lazyLoad
        open={!!actionItem}
        modalHeading={actionItem?.action === "edit" ? "Edit API key" : "New API key"}
        primaryButtonText="Save"
        primaryButtonDisabled={isSavingDataBridgesApiKey || isUpdatingDataBridgesApiKey}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSave}
        onRequestClose={() => {
          setActionItem(null)
          resetForm()
        }}
      >
        <div className="mt-1">
          <TextInput
            labelText="Name"
            placeholder="Enter API key name"
            value={form.keyName}
            onChange={e => setForm(prev => ({ ...prev, keyName: e.target.value }))}
            invalid={!!errors.keyName}
            invalidText={errors.keyName}
            className="custom-text-field"
          />
        </div>

        <div className="mt-3">
          <Input labelText="Expiration date" invalidText={errors.expiryDate} invalid={!!errors.expiryDate}>
            {() => (
              <SingleDatePicker
                displayFormat="DD/MM/YYYY"
                hideKeyboardShortcutsPanel
                showDefaultInputIcon
                inputIconPosition="after"
                numberOfMonths={1}
                onDateChange={newDate => setForm({ ...form, expiryDate: newDate?.toISOString() || "" })}
                focused={focused}
                onFocusChange={({ focused: isFocus }) => setFocused(isFocus)}
                date={form.expiryDate ? moment(form.expiryDate) : null}
                id="expiryDate"
                isOutsideRange={m => m.unix() < moment().unix()}
              />
            )}
          </Input>
        </div>

        <div className="mt-3">
          <Checkbox
            id="piiaccess"
            labelText="Allow access to PII data"
            name="piiaccess"
            checked={form.piiaccess}
            onChange={event =>
              setForm(prev => ({
                ...prev,
                piiaccess: event.target.checked,
              }))
            }
          />
        </div>

        <div className="mt-3">
          <label htmlFor="apiKeyStatusToggle" className="wfp--label">
            Status
          </label>
          <Toggle
            id="apiKeyStatusToggle"
            toggled={form.apiKeyStatusId === DataBridgesApiKeyStatus.ENABLED}
            labelA=""
            labelB={form.apiKeyStatusId === DataBridgesApiKeyStatus.ENABLED ? "Enabled" : "Disabled"}
            onToggle={(checked: boolean) =>
              setForm(prev => ({
                ...prev,
                apiKeyStatusId: checked ? DataBridgesApiKeyStatus.ENABLED : DataBridgesApiKeyStatus.DISABLED,
              }))
            }
            className="outlier-toggle"
          />
        </div>
      </Modal>

      <Modal
        lazyLoad
        open={!!deleteItem}
        type="danger"
        modalHeading="Confirm delete"
        primaryButtonText="Delete"
        primaryButtonDisabled={isDeletingDataBridgesApiKey}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleConfirmDelete}
        onRequestClose={() => setDeleteItem(null)}
      >
        {`Are you sure you want to delete API key "${deleteItem?.keyName || ""}"?`}
      </Modal>
    </div>
  )
}

export default DataBridgesApiKeysTab
